import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, HelpCircle, XCircle } from "lucide-react";

const COLOR_FOR = {
  green: {
    dot: "bg-emerald-400 ring-emerald-500/40",
    text: "text-emerald-300",
    pill: "border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10",
    Icon: CheckCircle2,
  },
  amber: {
    dot: "bg-amber-400 ring-amber-500/40",
    text: "text-amber-300",
    pill: "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10",
    Icon: AlertTriangle,
  },
  red: {
    dot: "bg-rose-500 ring-rose-500/40 animate-pulse",
    text: "text-rose-300",
    pill: "border-rose-500/50 bg-rose-500/10 hover:bg-rose-500/20",
    Icon: XCircle,
  },
  unknown: {
    dot: "bg-slate-500 ring-slate-500/40",
    text: "text-slate-400",
    pill: "border-slate-700 bg-slate-900 hover:bg-slate-800",
    Icon: HelpCircle,
  },
};

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "never";
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function LedgerHealthStrip({ onDotClick }) {
  const [strip, setStrip] = useState(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      const { data } = await api.get("/commissions/ledger/health-strip");
      setStrip(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Health strip failed");
    }
  }
  useEffect(() => { reload(); }, []);

  async function reRunAll() {
    if (!window.confirm(
      "Re-run every ledger health check now?\n\n" +
      "This fires the Syncro backfill + deep-scan in parallel — can take 30-60s " +
      "on a tenant with lots of partners. The strip will refresh when done."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/ledger/health-check/run-all");
      if (data.strip) setStrip(data.strip);
      toast.success(`Ran ${data.ran.length} check(s). Strip refreshed.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-run failed");
    } finally {
      setBusy(false);
    }
  }

  if (!strip) {
    return (
      <div
        data-testid="ledger-health-strip-loading"
        className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-500 font-mono"
      >
        Loading ledger health…
      </div>
    );
  }

  const overall = COLOR_FOR[strip.overall_status] || COLOR_FOR.unknown;
  const latest = strip.dots.map(d => d.last_run).filter(Boolean).sort().pop();

  return (
    <div
      data-testid="ledger-health-strip"
      className={`rounded-xl border ${overall.pill} px-4 py-3 flex items-center justify-between gap-4 flex-wrap`}
    >
      <div className="flex items-center gap-2 shrink-0">
        <overall.Icon className={`w-4 h-4 ${overall.text}`} />
        <span className={`text-xs uppercase tracking-widest font-semibold ${overall.text}`}>
          Ledger health
        </span>
        <span className="text-[11px] text-slate-500 font-mono">
          · last check {timeAgo(latest)}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0 justify-center">
        {strip.dots.map((d) => {
          const c = COLOR_FOR[d.status] || COLOR_FOR.unknown;
          return (
            <button
              key={d.label}
              type="button"
              data-testid={`health-dot-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => onDotClick && onDotClick(d)}
              title={`${d.detail}${d.last_run ? ` · ran ${timeAgo(d.last_run)}` : ""}`}
              className={`inline-flex items-center gap-2 rounded-full border ${c.pill} px-3 py-1 transition-colors cursor-pointer`}
            >
              <span className={`w-2 h-2 rounded-full ring-2 ${c.dot}`} />
              <span className={`text-xs font-semibold ${c.text}`}>{d.label}</span>
              <span className="text-[10px] text-slate-500 font-mono truncate max-w-[180px]">
                {d.detail}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="health-strip-rerun"
        onClick={reRunAll}
        disabled={busy}
        className="inline-flex items-center gap-1.5 border border-slate-800 bg-slate-900 hover:bg-slate-950 text-slate-300 disabled:opacity-60 px-3 py-1.5 rounded-md text-xs font-medium transition-colors shrink-0"
        title="Re-run every health check now (fires Syncro backfill + deep-scan in parallel)"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        {busy ? "Running…" : "Re-run all"}
      </button>
    </div>
  );
}
